import { ItemProps } from '@snack-uikit/list';
import * as cheerio from 'cheerio';
import { Element as DomElement } from 'domhandler/lib/node';
import { FC, ReactNode } from 'react';

import { AbsolutePath } from '@/types/navigation';
import { equals, isEmpty } from '@/utils/common';
import { skipFileExtension } from '@/utils/navigation';

import s from './styles.module.scss';
import type { ProcessItem, SideBarItemWrapper } from './types';

export const normalizeNavLink = (link?: string) => {
  const processLink = (link?: string) => {
    const replaced = link ? link.replace('..', '') : '/';

    if (replaced.startsWith('/')) return replaced;

    return `/${replaced}`;
  };
  const normalized = processLink(link);

  if (equals(normalized, '/') || normalized.endsWith('../')) return `${normalized}index`;
  return normalized;
};

const processNestedNavList = (
  $: cheerio.CheerioAPI,
  element: cheerio.Cheerio<DomElement>,
  currentUrl: AbsolutePath,
  processItem: ProcessItem,
  Wrapper: SideBarItemWrapper,
): ItemProps[] => {
  const result: ItemProps[] = [];
  element.children('li').each((_i, li) => {
    result.push(processItem($, $(li), currentUrl, Wrapper));
  });
  return result;
};

const processNavListItem: ProcessItem = ($, li, currentUrl, Wrapper: SideBarItemWrapper) => {
  const a = $(li).children('a.reference.internal').first();
  const title = a.text().trim();
  const link = a.attr('href') ?? '';
  const nestedList = $(li).children('ul').first();

  const hasNested = !isEmpty(nestedList);

  const normalizedLink = normalizeNavLink(link);

  const itemLink = skipFileExtension('all', `${currentUrl}${normalizedLink}`);

  const cleanLink = itemLink.endsWith('/') ? `${itemLink}index` : itemLink;

  const newItem: {
    items: ItemProps[];
    itemWrapRender?: FC<ReactNode>;
    type?: string;
    id: string | number;
    content: { option: string; className?: string };
  } = {
    content: { option: title, className: s.item_wrapper },
    id: cleanLink,
    itemWrapRender: item => (
      <Wrapper itemType='sidebar' itemLink={cleanLink} hasNested={hasNested}>
        {item}
      </Wrapper>
    ),
    items: [],
  };

  if (hasNested) {
    newItem.items = processNestedNavList($, nestedList, currentUrl, processNavListItem, Wrapper);
    newItem.type = 'collapse';
  }

  return newItem;
};

export const createSidebarItems = (
  htmlString: string,
  currentUrl: AbsolutePath,
  Wrapper: SideBarItemWrapper,
): ItemProps[] => {
  const $ = cheerio.load(htmlString);
  const firstList = $('ul').first();
  if (isEmpty(firstList)) {
    return [];
  }
  return processNestedNavList($, firstList, currentUrl, processNavListItem, Wrapper);
};
